/**
 * Number presentation shared by the distribution tools: APA-style statistics and
 * probabilities small enough to need scientific notation.
 *
 * Formatting goes through the caller's locale formatter (`useToolNumber()` in a widget),
 * so a German reader sees `0,0304`. These helpers only decide *how many* digits and
 * whether a leading zero survives — the APA rules — never which characters to use.
 */

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

/** Below this a probability is written in scientific notation rather than as 0.0000. */
export const SCIENTIFIC_BELOW = 0.0001;

const LEADING_ZERO = /^([−-]?)0(?=\D)/;

/**
 * A value at a fixed number of decimals, optionally without the leading zero.
 *
 * APA 7 (§6.36) drops the zero before the decimal point for a statistic that cannot
 * exceed 1 in absolute value — p, r, proportions — and keeps it for everything else.
 */
export function fixed(
  value: number,
  decimals: number,
  fmt: NumberFormatter,
  leadingZero = true
): string {
  const text = fmt(value, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return leadingZero ? text : text.replace(LEADING_ZERO, "$1");
}

/** `{ relation, text }` for an APA p-value: "= .032", "< .001", "> .999". */
export function apaP(
  p: number,
  fmt: NumberFormatter
): { relation: "=" | "<" | ">"; text: string } {
  if (p < 0.001) {
    return { relation: "<", text: fixed(0.001, 3, fmt, false) };
  }
  if (p >= 0.9995) {
    return { relation: ">", text: fixed(0.999, 3, fmt, false) };
  }
  return { relation: "=", text: fixed(p, 3, fmt, false) };
}

/** "p = .032" — the symbol is notation, not a word, so it is not a translatable string. */
export function apaPText(p: number, fmt: NumberFormatter): string {
  const { relation, text } = apaP(p, fmt);
  return `p ${relation} ${text}`;
}

/**
 * Degrees of freedom as APA prints them: whole numbers bare, Welch-style fractional df
 * to two decimals.
 */
export function formatDf(df: number, fmt: NumberFormatter): string {
  return Number.isInteger(df)
    ? fmt(df, { maximumFractionDigits: 0, useGrouping: false })
    : fmt(df, { maximumFractionDigits: 2, useGrouping: false });
}

/**
 * Mantissa and exponent for writing a small probability as m × 10ⁿ, with the mantissa
 * already rounded to `digits` significant digits (so 9.996e-5 becomes 1.00 × 10⁻⁴, not
 * 10.0 × 10⁻⁵).
 */
export function scientificParts(
  value: number,
  digits = 3
): { mantissa: number; exponent: number } {
  if (value === 0 || !Number.isFinite(value)) {
    return { mantissa: value, exponent: 0 };
  }
  let exponent = Math.floor(Math.log10(Math.abs(value)));
  let mantissa = Number((value / 10 ** exponent).toPrecision(digits));
  if (Math.abs(mantissa) >= 10) {
    mantissa /= 10;
    exponent += 1;
  }
  return { mantissa, exponent };
}

/** Whether a probability is shown as m × 10ⁿ rather than as a fixed decimal. */
export function needsScientific(value: number): boolean {
  return value > 0 && value < SCIENTIFIC_BELOW;
}

/** The exponent with a true minus sign, for rendering inside a superscript. */
export function exponentText(exponent: number, fmt: NumberFormatter): string {
  const digits = fmt(Math.abs(exponent), { maximumFractionDigits: 0 });
  return exponent < 0 ? `−${digits}` : digits;
}

const SUPERSCRIPT: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
  "-": "⁻",
};

/**
 * A probability as plain text, for interpolating into a sentence: four decimals, or
 * "1.78 × 10⁻³³" with a Unicode superscript exponent once four decimals would read 0.
 */
export function probabilityText(value: number, fmt: NumberFormatter): string {
  if (!needsScientific(value)) {
    return fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  }
  const { mantissa, exponent } = scientificParts(value);
  const power = [...String(exponent)].map((c) => SUPERSCRIPT[c] ?? c).join("");
  return `${fmt(mantissa, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} × 10${power}`;
}
